import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import { SignalsQuery } from "lib/generated/app-service-gql/graphql";
import { SignalCategory } from "lib/StotlesApi";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

const signalsQuery = graphql(`
  query signals {
    signals {
      id
      name
      category
      organisationGuid
    }
  }
`);

export type Signal = SignalsQuery["signals"][number];

/**
 * The current team's individual signal definitions (one per keyword/CPV code/competitor/partner),
 * each with its own id — as opposed to `useSignalSettings`, which returns the raw criteria.
 */
export function useSignals(
  options?: UseQueryOptions<SignalsQuery, unknown, SignalsQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery(["signals"], signalsQuery, [], {
    ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
    ...options,
  });

  return { data: data?.signals, ...rest };
}

export type NameKeys = "partnerNames" | "competitorNames" | "keywordNames" | "cpvCodeNames";
export type SignalCategories = {
  partners: Signal[];
  partnerNames: string[];
  competitors: Signal[];
  competitorNames: string[];
  keywords: Signal[];
  keywordNames: string[];
  cpvCodes: Signal[];
  cpvCodeNames: string[];
};

/** The team's signals grouped by category, with convenience name arrays per category. */
export function useSignalCategories(
  options?: UseQueryOptions<SignalsQuery, unknown, SignalsQuery, QueryKey>,
) {
  const { data: signals, ...rest } = useSignals(options);
  const sortedSignals = signals?.slice().sort((a, b) => a.name.localeCompare(b.name));
  const categories = {
    keywords: sortedSignals?.filter((s) => s.category === SignalCategory.KEYWORD) || [],
    partners: sortedSignals?.filter((s) => s.category === SignalCategory.PARTNER) || [],
    competitors: sortedSignals?.filter((s) => s.category === SignalCategory.COMPETITOR) || [],
    cpvCodes: sortedSignals?.filter((s) => s.category === SignalCategory.CPV_CODE) || [],
  };

  const data: SignalCategories = {
    ...categories,
    keywordNames: categories.keywords.map((p) => p.name),
    partnerNames: categories.partners.map((p) => p.name),
    competitorNames: categories.competitors.map((p) => p.name),
    cpvCodeNames: categories.cpvCodes.map((p) => p.name),
  };

  return { ...rest, data };
}
